import {
  isObjectTextSentinel,
  normalizeExcelTextCell,
  normalizeTextFieldValue,
} from '../../misc/textFieldFormatter';

// Per-row validation for an uploaded WAC xlsx (see CLAUDE.md "WAC import
// (xlsx upload)"). Returns the header names of the INVALID columns; an empty
// array means the row is valid.
//
//   - All columns are required EXCEPT GENERAL_COMMENTS.
//   - NDC must be a valid NDC format.
//   - DRUG_TYPE must match Generic|Brand (regular expression, tolerant of
//     capitalization and minor misspellings).
//   - WAC_EFFECTIVE_DATE (the *_DATE column) must be a valid date.
//   - The money columns (WAC_INCREASE_AMOUNT, WAC_AFTER_INCREASE,
//     WAC_HISTORY_1YR, WAC_HISTORY_2YRS, RD_COSTS) must be valid currency.
//   - COST_INCREASE_FACTORS is required narrative text (NOT currency).

const REQUIRED_COLUMNS = [
  'NDC',
  'DRUG_PROD_DESC',
  'DRUG_TYPE',
  'WAC_EFFECTIVE_DATE',
  'WAC_INCREASE_AMOUNT',
  'WAC_AFTER_INCREASE',
  'WAC_HISTORY_1YR',
  'WAC_HISTORY_2YRS',
  'COST_INCREASE_FACTORS',
  'RD_COSTS',
];

const DATE_COLUMNS = ['WAC_EFFECTIVE_DATE'];

const CURRENCY_COLUMNS = [
  'WAC_INCREASE_AMOUNT',
  'WAC_AFTER_INCREASE',
  'WAC_HISTORY_1YR',
  'WAC_HISTORY_2YRS',
  'RD_COSTS',
];

const ALL_COLUMNS = [...REQUIRED_COLUMNS, 'GENERAL_COMMENTS'];

// Misspelling-tolerant matchers: the intended word may drop a vowel or repeat a
// letter, but the characteristic consonant skeleton must remain.
const GENERIC_PATTERN = /^g+e*n+e*r+i*c+$/i;
const BRAND_PATTERN = /^b+r+a*n+d+$/i;
const CURRENCY_PATTERN = /^-?\d+(\.\d+)?$/;

// Compatibility aliases for existing WAC-focused callers/tests. The formatter
// itself is shared by every application text input and GraphQL string sink.
export const normalizeWacCellValue = normalizeTextFieldValue;
export const normalizeWacExcelCell = normalizeExcelTextCell;

const isSupportedCellValue = (column, normalized) =>
  normalized.supported &&
  (!(normalized.value instanceof Date) || DATE_COLUMNS.includes(column));

const isBlank = (value) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && value.trim() === '');

const isValidNdc = (value) => {
  const digits = String(value).replace(/\D/g, '');
  if (digits === '' || /^0+$/.test(digits)) {
    return false;
  }
  return digits.length <= 11;
};

const isValidDrugType = (value) => {
  const normalized = String(value).trim();
  return GENERIC_PATTERN.test(normalized) || BRAND_PATTERN.test(normalized);
};

const isValidDate = (value) => !Number.isNaN(Date.parse(String(value).trim()));

const isValidCurrency = (value) => {
  const normalized = String(value).replace(/[$,\s]/g, '');
  return (
    CURRENCY_PATTERN.test(normalized) &&
    Number.isFinite(Number(normalized))
  );
};

// Canonical NDC key (digits only, left-padded to 11) used to compare NDCs
// across rows the same way they are persisted, so `123` and `00000000123`
// collide as the same drug. Returns '' when there are no digits.
export const wacNdcKey = (value) => {
  const normalized = normalizeWacCellValue(value);
  if (!isSupportedCellValue('NDC', normalized)) {
    return '';
  }
  const digits = String(normalized.value).replace(/\D/g, '');
  return digits ? digits.padStart(11, '0') : '';
};

// `seenNdcs` (optional Set of canonical NDC keys from earlier rows) lets the
// validator flag a duplicate NDC across the uploaded rows. The caller seeds the
// set with `wacNdcKey(row.NDC)` after each accepted row; this function never
// mutates it.
export default function validateWacRow(row = {}, seenNdcs = null) {
  const invalid = [];
  const normalizedRow = {};

  // Optional means GENERAL_COMMENTS may be blank. Recognized ExcelJS objects
  // are validated using their displayed value; unknown/error objects are still
  // rejected and named in the upload modal rather than silently discarded.
  ALL_COLUMNS.forEach((column) => {
    const normalized = normalizeWacCellValue(row[column]);
    normalizedRow[column] = normalized.value;
    if (
      !isSupportedCellValue(column, normalized) ||
      isObjectTextSentinel(normalized.value)
    ) {
      invalid.push(column);
    }
  });

  REQUIRED_COLUMNS.forEach((column) => {
    if (!invalid.includes(column) && isBlank(normalizedRow[column])) {
      invalid.push(column);
    }
  });

  if (!invalid.includes('NDC') && !isValidNdc(normalizedRow.NDC)) {
    invalid.push('NDC');
  }

  if (
    !invalid.includes('DRUG_TYPE') &&
    !isValidDrugType(normalizedRow.DRUG_TYPE)
  ) {
    invalid.push('DRUG_TYPE');
  }

  DATE_COLUMNS.forEach((column) => {
    if (!invalid.includes(column) && !isValidDate(normalizedRow[column])) {
      invalid.push(column);
    }
  });

  CURRENCY_COLUMNS.forEach((column) => {
    if (!invalid.includes(column) && !isValidCurrency(normalizedRow[column])) {
      invalid.push(column);
    }
  });

  // Duplicate NDC across rows: only meaningful for an otherwise valid NDC when
  // a set of previously-seen NDC keys is supplied.
  if (
    !invalid.includes('NDC') &&
    seenNdcs &&
    typeof seenNdcs.has === 'function' &&
    seenNdcs.has(wacNdcKey(normalizedRow.NDC))
  ) {
    invalid.push('NDC');
  }

  return invalid;
}
